/**
 * `dependency` (Dependent On) — the predecessors of this item.
 *
 * Captured: monday's picker is a **574 × 650** card, radius 16, with a `Cancel` 82 × 40 and a
 * `Save` 67 × 40 (`editors/dependency/editor.json`, round 3) over an item list; the tier-2 run
 * caught its `Select a Column` step. As with `board_relation`, this build has no cross-board
 * item search yet, so the editor takes item ids — the shape the value stores and the shape
 * `{"dependent_on": {"item_ids": […]}}` writes `[09§A.7]` — and says so (q342).
 *
 * The **modes** (`flexible` · `strict` · `no_action`) live in the column settings and drive the
 * worker's auto-shift; they could not be measured on the sandbox (q337).
 */
import {
  DEPENDENCY_MODES,
  dependencyMode,
  getColumnType,
  type DependencySettings,
  type DependencyValue,
} from '@elizmonday/shared';
import { CellPopover, PickerSearch, useEditorDraft } from '../primitives';
import { DependencySettingsDialog } from './SettingsDialog';
import { defineColumnUi } from '../types';

const shared = getColumnType('dependency');

/** Measured: 574 × 650 (`editors/dependency/editor.json`). */
export const DEPENDENCY_PICKER_WIDTH = 574;

export const dependencyColumnUi = defineColumnUi<DependencyValue, DependencySettings>({
  type: 'dependency',
  icon: 'columnSubtasks',
  gallery: { group: 'advanced', keywords: ['dependency', 'dependent on', 'predecessor', 'gantt'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'left',
  groupable: false,

  Cell: ({ value }) => {
    const ids = value?.item_ids ?? [];
    if (ids.length === 0) return <span className="ez-cell__empty" aria-hidden="true" />;
    return (
      <span className="ez-chips" data-align="left">
        {ids.slice(0, 2).map((id) => (
          <span key={id} className="ez-chip ez-chip--relation">{`#${id}`}</span>
        ))}
        {ids.length > 2 ? <span className="ez-chip ez-chip--more">{`+${ids.length - 2}`}</span> : null}
      </span>
    );
  },

  /** **P-282**: the id list follows the store while untouched; an untouched dismiss writes
   * nothing (`useEditorDraft`). */
  Editor: ({ value, settings, onCommit, onCancel }) => {
    const editor = useEditorDraft((value?.item_ids ?? []).join(', '));
    const draft = editor.draft;
    const commit = (): void => {
      const ids = draft
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => /^\d+$/.test(entry));
      onCommit(ids.length === 0 ? null : { item_ids: ids });
    };
    return (
      <CellPopover
        label="Dependent on"
        width={DEPENDENCY_PICKER_WIDTH}
        onDismiss={() => editor.dismiss(commit, onCancel)}
        onCancel={onCancel}
      >
        <PickerSearch value={draft} onChange={editor.set} placeholder="Item ids, comma separated" />
        <p className="ez-two-field__hint">{`Mode: ${dependencyMode(settings)}`}</p>
      </CellPopover>
    );
  },

  SettingsDialog: DependencySettingsDialog,

  fromClipboard(text, settings) {
    const ids = text
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => /^\d+$/.test(entry));
    return ids.length === 0 ? null : (shared.parseValue({ item_ids: ids }, settings) as DependencyValue | null);
  },
  toClipboard(value) {
    return (value?.item_ids ?? []).join(', ');
  },
  bucketsOf(value) {
    return ((value as DependencyValue | null)?.item_ids ?? []).map((id) => ({ key: id, label: `#${id}` }));
  },
});

export { DEPENDENCY_MODES };
